import { useAuth } from "../router/AuthContext";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";

export default function ProtectedRoute() {
  const { user, loading, checkTokenExpiry } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(false);
  const lastCheckRef = useRef<number>(0);
  const checkingRef = useRef<boolean>(false);

  // Debounced token check to prevent excessive calls
  const debouncedCheckToken = async () => {
    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckRef.current;

    // Only check if it's been at least 2 seconds since last check
    if (timeSinceLastCheck < 2000 || checkingRef.current) {
      return;
    }

    if (user && !loading) {
      checkingRef.current = true;
      setChecking(true);

      try {
        await checkTokenExpiry();
        lastCheckRef.current = now;
      } catch (error) {
        console.error("Token check failed:", error);
      } finally {
        setChecking(false);
        checkingRef.current = false;
      }
    }
  };

  // Check token validity only on route changes (less frequent)
  useEffect(() => {
    debouncedCheckToken();
  }, [location.pathname]); // Removed user and loading dependencies to reduce calls

  // Handle browser back/forward navigation with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handlePopState = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        debouncedCheckToken();
      }, 300); // 300ms debounce
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      clearTimeout(timeoutId);
    };
  }, [user]);

  // Handle page focus with longer debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleFocus = () => {
      if (document.visibilityState === "visible") {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          debouncedCheckToken();
        }, 500); // 500ms debounce for focus events
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleFocus);
      clearTimeout(timeoutId);
    };
  }, [user]);

  // Show loading only during initial load, not during token checks
  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
        <span className='ml-2'>Loading...</span>
      </div>
    );
  }

  // Don't show checking state to prevent flickering
  return user ? (
    <Outlet />
  ) : (
    <Navigate to='/log-in' state={{ from: location }} replace />
  );
}
